import React, { useState } from 'react';
import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import SaveFilterIcon from '@mui/icons-material/BookmarkAddOutlined';
import SavedFilterIcon from '@mui/icons-material/BookmarkBorder';
import ManageIcon from '@mui/icons-material/SettingsOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import RemoveIcon from '@mui/icons-material/Close';
import ConfirmDialog from './ConfirmDialog';
import { CHIP_COLORS, chipColorSx } from './DashboardHeader';
import { DashboardFilters, SavedFilter, SavedFilterDetails } from '../types/DashboardType';

// Same limit as the backend (SavedFilterService), on the user's own filters only
export const MAX_SAVED_FILTERS = 10;

/** Same selected values, ignoring order and columns with nothing selected. */
export function sameFilters(a: DashboardFilters, b: DashboardFilters): boolean {
  const normalise = (filters: DashboardFilters) =>
    JSON.stringify(
      Object.keys(filters)
        .filter(name => filters[name].length > 0)
        .sort()
        .map(name => [name, [...filters[name]].sort()]),
    );
  return normalise(a) === normalise(b);
}

export function hasFilters(filters: DashboardFilters): boolean {
  return !sameFilters(filters, {});
}

/** Identifies a filter: its status for the default ones (which have no id), its id for the user's own. */
export function filterKey(savedFilter: SavedFilter): string {
  return savedFilter.defaultStatus ?? String(savedFilter.id);
}

/** The chip's colours, and the bookmark icon for the user's own saved filters (not the default ones). */
function chipLook(filter: { color: string | null; isDefault?: boolean }, selected: boolean) {
  const colors = filter.color ? CHIP_COLORS[filter.color] : undefined;
  return {
    icon: filter.isDefault ? undefined : <SavedFilterIcon fontSize="small" />,
    sx: { fontWeight: selected ? 600 : 400, ...chipColorSx(colors, selected), ...(colors && { '& .MuiChip-icon': { color: 'inherit' } }) },
  };
}

/**
 * The default filters (one per status) and the user's own saved filters as a row of chips: clicking one replaces the filters
 * with its own, clicking the selected one clears them. Each chip shows how many rows its filters match.
 * "Save filter" shows when filters are set that aren't saved yet. The cog at the end opens the "Filters" dialog to
 * edit or remove them, and to reset the default ones.
 */
export function SavedFilters(props: {
  savedFilters: SavedFilter[];
  currentFilters: DashboardFilters;
  onApply: (filters: DashboardFilters) => void;
  onSave: (details: SavedFilterDetails) => void;
  onUpdate: (savedFilter: SavedFilter, details: SavedFilterDetails) => void;
  onRemove: (savedFilter: SavedFilter) => void;
  counts: Record<string, number>;
  onResetDefaults: () => void;
  // Readable filters, e.g. "Status: Pending", as the table's filter chips show them
  describeFilters: (filters: DashboardFilters) => string[];
}) {
  const { savedFilters, currentFilters, onApply, onSave, onUpdate, counts } = props;
  const [saving, setSaving] = useState(false);
  const [managing, setManaging] = useState(false);

  const shown = savedFilters.filter(f => !f.hidden);
  const alreadySaved = shown.some(savedFilter => sameFilters(savedFilter.filters, currentFilters));
  const atLimit = savedFilters.filter(f => !f.defaultStatus).length >= MAX_SAVED_FILTERS;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }} role="group" aria-label="Saved filters">
      {shown.map(savedFilter => {
        const selected = sameFilters(savedFilter.filters, currentFilters);
        return (
          <Chip
            key={filterKey(savedFilter)}
            label={`${savedFilter.name} · ${counts[filterKey(savedFilter)] ?? 0}`}
            {...chipLook({ ...savedFilter, isDefault: !!savedFilter.defaultStatus }, selected)}
            variant={selected ? 'filled' : 'outlined'}
            aria-pressed={selected}
            onClick={() => onApply(selected ? {} : savedFilter.filters)}
          />
        );
      })}
      {hasFilters(currentFilters) && !alreadySaved && (
        <Tooltip title={atLimit ? `You can save up to ${MAX_SAVED_FILTERS} filters, remove one first` : ''}>
          <span>
            <Chip icon={<SaveFilterIcon />} label="Save filter" variant="outlined" disabled={atLimit} onClick={() => setSaving(true)} />
          </span>
        </Tooltip>
      )}
      <Tooltip title="Manage filters">
        <IconButton size="small" aria-label="Manage filters" onClick={() => setManaging(true)} sx={{ color: 'text.secondary' }}>
          <ManageIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {saving && (
        <SavedFilterDialog
          title="Save filter"
          initial={{ name: '', color: null }}
          filterDescriptions={props.describeFilters(currentFilters)}
          onClose={() => setSaving(false)}
          onSave={onSave}
        />
      )}
      {managing && <ManageSavedFiltersDialog {...props} onClose={() => setManaging(false)} />}
    </Box>
  );
}

/** The default filters and the user's own saved filters, to edit (name, colour) or remove them, and to reset the defaults. */
function ManageSavedFiltersDialog(props: {
  savedFilters: SavedFilter[];
  onUpdate: (savedFilter: SavedFilter, details: SavedFilterDetails) => void;
  onRemove: (savedFilter: SavedFilter) => void;
  onResetDefaults: () => void;
  describeFilters: (filters: DashboardFilters) => string[];
  onClose: () => void;
}) {
  const { savedFilters, onUpdate, onRemove, onResetDefaults, describeFilters, onClose } = props;
  const [editing, setEditing] = useState<SavedFilter>();
  const [removing, setRemoving] = useState<SavedFilter>();
  const [resetting, setResetting] = useState(false);
  const defaultFilters = savedFilters.filter(f => f.defaultStatus && !f.hidden);
  const ownFilters = savedFilters.filter(f => !f.defaultStatus);
  // Reset only shows once a default filter was removed, renamed or recoloured
  const defaultsChanged = savedFilters.some(f => f.defaultStatus && (f.hidden || f.customised));

  const sectionTitle = (title: string) => (
    <Typography variant="subtitle2" sx={{ flex: 1 }}>
      {title}
    </Typography>
  );
  const filterRow = (savedFilter: SavedFilter) => {
    const descriptions = describeFilters(savedFilter.filters);
    return (
      <Box key={filterKey(savedFilter)} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Chip label={savedFilter.name} {...chipLook({ ...savedFilter, isDefault: !!savedFilter.defaultStatus }, false)} variant="outlined" />
        <Tooltip title={<FilterList descriptions={descriptions} />}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap>
            {descriptions.join(', ')}
          </Typography>
        </Tooltip>
        <IconButton size="small" aria-label={`Edit "${savedFilter.name}"`} onClick={() => setEditing(savedFilter)}>
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" aria-label={`Remove "${savedFilter.name}"`} onClick={() => setRemoving(savedFilter)}>
          <RemoveIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Filters</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {sectionTitle('Default filters')}
          {defaultsChanged && (
            <Button size="small" onClick={() => setResetting(true)}>
              Reset
            </Button>
          )}
        </Box>
        {defaultFilters.map(filterRow)}

        <Box sx={{ mt: 3, mb: 0.5 }}>{sectionTitle('Your saved filters')}</Box>
        {ownFilters.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            None yet. Set filters with the filter icon above the table, then click Save filter.
          </Typography>
        )}
        {ownFilters.map(filterRow)}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="contained" onClick={onClose}>
          Done
        </Button>
      </DialogActions>

      {editing && (
        <SavedFilterDialog
          title="Edit filter"
          initial={editing}
          isDefault={!!editing.defaultStatus}
          filterDescriptions={describeFilters(editing.filters)}
          filtersNote="To change them, set new filters and save them as a new filter."
          onClose={() => setEditing(undefined)}
          onSave={details => onUpdate(editing, details)}
        />
      )}
      <ConfirmDialog
        title={`Remove the filter "${removing?.name}"?`}
        open={!!removing}
        setOpen={() => setRemoving(undefined)}
        onConfirm={() => removing && onRemove(removing)}
        cancelText="Cancel"
        confirmText="Remove"
      >
        It will no longer show at the top of your dashboard.
      </ConfirmDialog>
      <ConfirmDialog
        title="Reset the default filters?"
        open={resetting}
        setOpen={setResetting}
        onConfirm={onResetDefaults}
        cancelText="Cancel"
        confirmText="Reset"
      >
        Puts Ready To Start, Pending and Complete back to their original name and colour. Your own filters stay as they are.
      </ConfirmDialog>
    </Dialog>
  );
}

/** One filter per line, e.g. "Status: Pending" then "Tag: bas". */
function FilterList(props: { descriptions: string[] }) {
  return (
    <>
      {props.descriptions.map(description => (
        <div key={description}>{description}</div>
      ))}
    </>
  );
}

/** Name and colour of a saved filter, with a preview of its chip and the filters it keeps (which can't change once saved). */
function SavedFilterDialog(props: {
  title: string;
  initial: SavedFilterDetails;
  // A default filter: its chip has no bookmark
  isDefault?: boolean;
  filterDescriptions?: string[];
  filtersNote?: string;
  onClose: () => void;
  onSave: (details: SavedFilterDetails) => void;
}) {
  const { title, initial, isDefault, filterDescriptions, filtersNote, onClose, onSave } = props;
  const [details, setDetails] = useState<SavedFilterDetails>({ name: initial.name, color: initial.color });
  const name = details.name.trim();

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <form
        onSubmit={event => {
          event.preventDefault();
          if (name) {
            onSave({ ...details, name });
            onClose();
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600 }}>{title}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            margin="dense"
            label="Name"
            placeholder="e.g. BAS clients"
            value={details.name}
            onChange={event => setDetails({ ...details, name: event.target.value })}
            inputProps={{ maxLength: 50, 'data-testid': 'saved-filter-name' }}
            helperText="Keeps the filters as a chip at the top, so you can switch to them with one click."
          />
          <Typography variant="body2" sx={{ mt: 1.5, mb: 0.5 }}>
            Colour
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {[null, ...Object.keys(CHIP_COLORS)].map(color => (
              <IconButton
                key={color || 'none'}
                aria-label={color || 'No colour'}
                aria-pressed={details.color === color}
                onClick={() => setDetails({ ...details, color })}
                sx={{ width: 32, height: 32, p: 0, border: '2px solid', borderColor: details.color === color ? 'primary.main' : 'transparent' }}
              >
                <Box
                  sx={{
                    width: 18,
                    height: 18,
                    flexShrink: 0,
                    borderRadius: '50%',
                    backgroundColor: color ? CHIP_COLORS[color].border : '#fff',
                    border: color ? 'none' : '1px dashed #9e9e9e',
                  }}
                />
              </IconButton>
            ))}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Chip label={name || 'Preview'} {...chipLook({ color: details.color, isDefault }, true)} variant="filled" />
          </Box>
          {filterDescriptions && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2">Filters</Typography>
              <Typography variant="body2" color="text.secondary" component="div" data-testid="saved-filter-filters">
                <FilterList descriptions={filterDescriptions} />
              </Typography>
              {filtersNote && (
                <Typography variant="caption" color="text.secondary">
                  {filtersNote}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={!name}>
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
